// The toast queue (spec §3) as pure functions over { items, paused } and an injected
// clock (`now`, in ms). Each item carries `remaining` (ms left, or null when sticky)
// and `since` (when its countdown last (re)started, or null while paused). Every
// function returns the SAME object when nothing changed, so React's
// useSyncExternalStore skips the re-render.
export const MAX_VISIBLE = 4;
export const DEFAULT_AUTOCLOSE = 5000;
export const DANGER_AUTOCLOSE = 8000;

export function emptyToasts() {
  return { items: [], paused: false, tabHidden: false };
}

// A toast's clock runs ONLY while it can be seen (TOA-4): it is one of the newest
// MAX_VISIBLE, the pointer/focus hold is off, and the tab is shown. reconcile() is the
// one place that starts and stops clocks: a stopping clock banks the time it spent into
// `remaining`; a starting one stamps `since`. Same object back when nothing changed.
export function reconcile(state, now) {
  const visible = new Set(state.items.slice(-MAX_VISIBLE));
  const held = state.paused || state.tabHidden;
  let changed = false;
  const items = state.items.map((t) => {
    if (t.remaining === null) return t;
    const runs = !held && visible.has(t);
    if (runs && t.since === null) { changed = true; return { ...t, since: now }; }
    if (!runs && t.since !== null) {
      changed = true;
      return { ...t, remaining: Math.max(0, t.remaining - (now - t.since)), since: null };
    }
    return t;
  });
  return changed ? { ...state, items } : state;
}

// false: sticky. A positive finite number: as given. Anything else: the tone's default
// (errors get longer, they need reading time).
export function autoCloseFor(tone, autoClose) {
  if (autoClose === false) return null;
  if (typeof autoClose === 'number' && Number.isFinite(autoClose) && autoClose > 0) return autoClose;
  return tone === 'danger' ? DANGER_AUTOCLOSE : DEFAULT_AUTOCLOSE;
}

export function addToast(state, toast, now) {
  const item = { ...toast, remaining: autoCloseFor(toast.tone, toast.autoClose), since: null };
  return reconcile({ ...state, items: [...state.items, item] }, now);
}

// `now` matters when closing promotes a hidden toast (its clock starts then).
export function closeToast(state, id, now) {
  const items = state.items.filter((t) => t.id !== id);
  return items.length === state.items.length ? state : reconcile({ ...state, items }, now);
}

export function pauseToasts(state, now) {
  if (state.paused) return state;
  return reconcile({ ...state, paused: true }, now);
}

export function resumeToasts(state, now) {
  if (!state.paused) return state;
  return reconcile({ ...state, paused: false }, now);
}

// document.hidden, from the visibilitychange event: every clock stops while the tab is
// hidden and resumes with its remaining time.
export function setTabHidden(state, hidden, now) {
  if (Boolean(state.tabHidden) === Boolean(hidden)) return state;
  return reconcile({ ...state, tabHidden: Boolean(hidden) }, now);
}

export function expireToasts(state, now) {
  if (state.paused || state.tabHidden) return state;
  const items = state.items.filter((t) => t.remaining === null || t.since === null || now - t.since < t.remaining);
  return items.length === state.items.length ? state : reconcile({ ...state, items }, now);
}

export function nextDeadline(state, now) {
  if (state.paused || state.tabHidden) return null;
  let soonest = null;
  for (const t of state.items) {
    if (t.remaining === null || t.since === null) continue;
    const left = t.remaining - (now - t.since);
    if (soonest === null || left < soonest) soonest = left;
  }
  return soonest === null ? null : Math.max(0, soonest);
}

// The newest MAX_VISIBLE, oldest first (the newest sits nearest the bottom-right corner);
// older ones are counted, their clocks wait (see reconcile), and they appear as newer ones leave.
export function visibleToasts(state) {
  const visible = state.items.slice(-MAX_VISIBLE);
  return { visible, hidden: state.items.length - visible.length };
}
